import { useCommunityDataPublic } from "@/stores/communityDataPublic";
import { useFormDataStore } from "@/stores/formDataStore";
import { computed } from "vue";
import axios from "axios";
import { METERING_POINT_DIRECTION, CONNECTION_TYPE } from "./constants/meteringPoint";
import {type ComposerTranslation} from "vue-i18n";
import {
  useSnackbarStore,
  SnackbarEnum,
} from '@es-bill/es-bill-common-components';
import { MemberType } from '../../types/src/enums';

/**
 * Interface for application configuration
 */
interface ApplicationConfig {
  FRONTEND_BASEURL: string;
  BACKEND_BASEURL: string;
  VERSION_IDENTIFIER: string;
}

/**
 * Metering point data for API submission
 */
interface SubmissionMeteringPoint {
  nameFriendly: string;
  meteringPoint: string;
  meteringPointDirection: string;
  ecnDso?: string; // Optional field for DSO ecn needed for CC/16b
  participationFactor?: number; // Participation factor as percentage string
  addressPostal: {
    name: string;
    address: string;
    addressExtra: string;
    zip: string;
    city: string;
    country: string;
  };
  assumptionEnergyPerYearKwh: string;
  energyStorageKwh: string;
  // Fields for generation metering points
  generationType?: string;
  generationConfiguration?: string;
  lineCapacity?: number;
}

/**
 * Applicant data for API submission
 */
interface SubmissionApplicant {
  nameFriendly: string;
  legal: string;
  confirmedSmeState: boolean | null;
  contactData: {
    nameFriendly: string;
    emailAddress: string;
    phoneNumber: string;
    nameFirst: string;
    nameLast: string;
    dateOfBirth: string;
    companyName: string;
  };
  billingData: {
    addressPostal: {
      name: string;
      address: string;
      addressExtra: string;
      zip: string;
      city: string;
      country: string;
    };
    identification: string;
    identificationType: string;
    taxInfo: {
      taxSelection: string;
      taxRatePercentPoints: number;
      taxNote: string;
      uid: string;
    };
    wireInfo: {
      accountHolder: string;
      iban: string;
      bic?: string;
      sepaMandateSignatureDate?: string;
    };
  };
}

/**
 * Custom agreement consent for API submission
 */
interface SubmissionCustomAgreementConsent {
  agreed: boolean;
  description: string;
  link?: string;
}

/**
 * Complete submission data structure for API
 */
interface SubmissionData {
  applicant: SubmissionApplicant;
  meteringPointsAssociated: SubmissionMeteringPoint[];
  notes: string;
  submissionDateTime: Date;
  agreedJoin: boolean;
  agreedDataProcessing: boolean;
  agreedNewsletterConsent?: boolean;
  customAgreementConsents: SubmissionCustomAgreementConsent[];
}

/**
 * Hook for data processing functions
 */
export function useDataProcessing(t: ComposerTranslation) {
  /**
   * Process form data and submit to backend
   * @param appConfig Application configuration object
   * @returns True if successful, error message if failed
   */
  async function processAndSubmitData(appConfig: ApplicationConfig): Promise<true | string> {
    const communityDataPublicStore = useCommunityDataPublic();
    const communityPublic = computed(() => communityDataPublicStore.communityActive);

    const snackbarStore = useSnackbarStore();
    const formDataStore = useFormDataStore();
    const formData = formDataStore.formData;

    // Validate agreements
    if (formData.agreedJoin === false || formData.agreedDataProcessing === false) {
      return t('dataProcessing.agreementsRequiredError');
    }

    // Process metering points
    const meteringPointsAssociated: SubmissionMeteringPoint[] = formData.meteringPointsAssociated
      .filter(mp => mp.meteringPoint)
      .map(mp => {
        const submissionMp: SubmissionMeteringPoint = {
          nameFriendly: mp.nameFriendly,
          meteringPoint: mp.meteringPoint,
          meteringPointDirection: mp.meteringPointDirection,
          ecnDso: mp.ecnDso && mp.ecnDso.trim() !== "" ? mp.ecnDso : undefined,
          participationFactor: mp.participationFactor,
          addressPostal: mp.addressPostal,
          assumptionEnergyPerYearKwh: mp.assumptionEnergyPerYearKwh,
          energyStorageKwh: mp.meteringPointDirection === METERING_POINT_DIRECTION.CONSUMPTION ? "" : mp.energyStorageKwh
        };

        // Add generation-specific fields for non-consumption metering points
        if (mp.meteringPointDirection !== METERING_POINT_DIRECTION.CONSUMPTION) {
          submissionMp.generationType = mp.plant.generationType;
                      submissionMp.generationConfiguration = mp.plant.generationConfiguration === CONNECTION_TYPE.FULL_FEEDER ? "FULLFEEDER" : "SURPLUSFEEDER";
          submissionMp.lineCapacity = Number(mp.plant.lineCapacity);
        }

        return submissionMp;
      });

    // Build applicant data based on company type
    let nameFriendly: string;
    let legal: string;
    let billingName: string;
    let identificationType: string;
    let identification: string;

    if (formData.isCompany === MemberType.PRIVATE) {
      nameFriendly = `${formData.contactData.nameFirst} ${formData.contactData.nameLast}`;
      legal = "Privatperson";
      billingName = nameFriendly;
      identificationType = "DateOfBirth";
      identification = formData.contactData.dateOfBirth;
    } else if (formData.legal === "Einpersonenunternehmen") {
      nameFriendly = formData.contactData.companyName;
      legal = formData.legal;
      billingName = formData.contactData.companyName;
      identificationType = formData.billingData.identificationType === "DateOfBirth" ? "DateOfBirth" : formData.billingData.identificationType;
      identification = formData.billingData.identificationType === "DateOfBirth" ? formData.contactData.dateOfBirth : formData.billingData.identification;
    } else {
      nameFriendly = formData.contactData.companyName;
      legal = formData.legal;
      billingName = formData.contactData.companyName;
      identificationType = formData.billingData.identificationType;
      identification = formData.billingData.identification;
    }

    const applicant: SubmissionApplicant = {
      nameFriendly,
      legal,
      confirmedSmeState: formData.confirmedSmeState,
      contactData: {
        nameFriendly: `${formData.contactData.nameFirst} ${formData.contactData.nameLast}`,
        emailAddress: formData.contactData.emailAddress,
        phoneNumber: formData.contactData.phoneNumber,
        nameFirst: formData.contactData.nameFirst,
        nameLast: formData.contactData.nameLast,
        dateOfBirth: formData.contactData.dateOfBirth,
        companyName: formData.contactData.companyName
      },
      billingData: {
        addressPostal: {
          ...formData.billingData.addressPostal,
          name: billingName
        },
        identification,
        identificationType,
        taxInfo: {
          taxSelection: formData.billingData.taxInfo.taxSelection,
          taxRatePercentPoints: Number(formData.billingData.taxInfo.taxRatePercentPoints),
          taxNote: formData.billingData.taxInfo.taxNote,
          uid: formData.billingData.taxInfo.uid
        },
        // Include SEPA mandate details if user agreed upfront
        wireInfo: {
          ...formData.billingData.wireInfo,
          ...(formData['agreedSepa'] ? {
            sepaMandateSignatureDate: Date.now().toString()
          } : {})
        }
      }
    };

    // Build custom agreement consents
    const cp: any = communityPublic.value;
    if (!cp) {
      return t('dataProcessing.communityDataUnavailable');
    }
    const communityName = cp?.addressPostal?.name || '';
    const communityLegalName = cp?.addressPostal?.name || '';
    const communityLegalType = cp?.legal || '';
    const communityType = cp?.communityType || '';
    const customAgreements = cp?.customAgreements || [];

    const consents = formDataStore.getCustomAgreements(
      communityName,
      communityLegalName,
      communityLegalType,
      communityType,
      customAgreements
    );

    const customAgreementConsents: SubmissionCustomAgreementConsent[] = consents
      .filter(c => c.id.startsWith('customAgreement'))
      .map(c => ({
        agreed: formData[c.id],
        description: c.description,
        link: c.link?.url
      }));

    // Build final submission data
    const dataToSend: SubmissionData = {
      applicant,
      meteringPointsAssociated,
      notes: formData.notes,
      submissionDateTime: new Date(),
      agreedJoin: formData.agreedJoin,
      agreedDataProcessing: formData.agreedDataProcessing,
      agreedNewsletterConsent: formData.agreedNewsletterConsent || false,
      customAgreementConsents
    };

    // Submit data to backend
    try {
      const backendBaseUrl = appConfig.BACKEND_BASEURL;

      // Check if community data is available
      if (!communityPublic.value || !communityPublic.value.ecn) {
        return t('dataProcessing.communityDataUnavailable');
      }

      const response = await axios.post(
        backendBaseUrl + "registration/members/" + communityPublic.value.ecn,
        dataToSend,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status !== 204) {
        return t('dataProcessing.unexpectedServerResponse', { details: response.statusText });
      } else {
        snackbarStore.showSnackbar(t('applicationReceived'), "", SnackbarEnum.Success, 5000, [])
      }

    } catch (e: any) {
      console.log(e);
      return t('dataProcessing.submitFailed', { details: e.message });
    }

    // Reset form data

    if (!(import.meta.env?.VITE_DEV_MODE === 'true'))  // In dev mode, keep the data for easier testing
      formDataStore.$reset();

    return true;
  }

  return { processAndSubmitData };
}
